import { useCallback, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useHistory } from "react-router";
import { Modal, Button, Input, Select, Divider, Icon } from "semantic-ui-react";
import { toast } from "../..";
import {
    getCroppedImg,
    ImageDropzoneCropper,
} from "../../components/Dropzone/ImageDropzoneCropper";
import { TranslateCountryProps } from "../../i18n/TranslateProps";
import {
    constructDate,
    dateInit,
    deconstructDate,
    getCountryFromIssuingAuthority,
} from "../../models/common";
import { IIDDataSet, newIDDataSetInit } from "../../models/crewMember";
import { newEidasInformationInit } from "../../models/eidasInformation";
import { DateInput } from "../../components/DateInput/DateInput";
import { createCrewMember, editCrewMember } from "../../api/crewMembers";
import { AppState } from "../../store/configureStore";
import { useSelector } from "react-redux";
import { Crop } from "react-image-crop";
import { SearchableIcs } from "../../models/certificate";
import { getSearchableIcsUserProps } from "../../api/certificates";

type Props = {
    open: boolean;
    closeModal: () => void;
    idDataSet?: IIDDataSet;
    fetchCrewMember?: () => Promise<void>;
};

export const CrewMemberFormModal = ({ open, closeModal, idDataSet, fetchCrewMember }: Props) => {
    const user = useSelector((state: AppState) => state.user);
    const { countries, nationalities } = useSelector((state: AppState) => state.nationalities);
    const intl = useIntl();
    const history = useHistory();
    const [newIdDataSet, setNewIdDataSet] = useState(idDataSet ? idDataSet : newIDDataSetInit);
    const [loading, setLoading] = useState(false);
    const [loadingSelect, setLoadingSelect] = useState(false);
    const [dateOfBirth, setDateOfBirth] = useState(
        idDataSet ? deconstructDate(idDataSet.dateOfBirth) : dateInit
    );
    const [crop, setCrop] = useState<Crop>();
    const [placeOfBirthOptions, setPlaceOfBirthOptions] = useState<SearchableIcs[]>([]);
    const [placeOfBirthOptionsNonLat, setPlaceOfBirthOptionsNonLat] = useState<SearchableIcs[]>([]);
    const [zipCodeOptions, setZipCodeOptions] = useState<SearchableIcs[]>([]);
    const [cityOptions, setCityOptions] = useState<SearchableIcs[]>([]);

    const handleCloseModal = () => {
        setNewIdDataSet(idDataSet ? idDataSet : newIDDataSetInit);
        closeModal();
        setCrop(undefined);
    };

    const handleAddCrewMember = async () => {
        setLoading(true);
        try {
            newIdDataSet.added = new Date().toISOString();
            newIdDataSet.dateOfBirth = constructDate(dateOfBirth);
            const img = (await getCroppedImg(crop)) as File;
            const res = await createCrewMember(newIdDataSet, img, false);
            const guid = (await res.data).replace(/"/g, "");
            toast(intl.formatMessage({ id: "createdCrewMember" }), true);
            history.push("/crew-members/" + guid);
        } catch {
            toast(intl.formatMessage({ id: "unableToCreateCrewMember" }), false);
        } finally {
            setLoading(false);
            handleCloseModal();
        }
    };

    const handleEditCrewMember = async () => {
        setLoading(true);
        try {
            newIdDataSet.dateOfBirth = constructDate(dateOfBirth);
            const img = crop ? ((await getCroppedImg(crop)) as File) : undefined;
            await editCrewMember(newIdDataSet, img);
            toast(intl.formatMessage({ id: "crewMemberEdited" }), true);
            fetchCrewMember && (await fetchCrewMember());
        } catch {
            toast(intl.formatMessage({ id: "unableToEditCrewMember" }), false);
        } finally {
            setLoading(false);
            handleCloseModal();
        }
    };

    const handleEidasInformationInputChange = async (e: any, index: number) => {
        const newEidasInformation = [...newIdDataSet.eidasInformation];
        newEidasInformation[index].personIdentifier = e.target.value;
        setNewIdDataSet({ ...newIdDataSet, eidasInformation: [...newEidasInformation] });
    };

    const handleAddNewEidasInformation = async () => {
        setNewIdDataSet({
            ...newIdDataSet,
            eidasInformation: newIdDataSet.eidasInformation.concat({ ...newEidasInformationInit }),
        });
    };

    const getSearchableIcs = useCallback(async () => {
        setLoadingSelect(true);
        const response = await getSearchableIcsUserProps();
        setPlaceOfBirthOptions(response.placeOfBirth);
        setPlaceOfBirthOptionsNonLat(response.placeOfBirthNonLat);
        setZipCodeOptions(response.zipCode);
        setCityOptions(response.city);
        setLoadingSelect(false);
    }, []);

    useEffect(() => {
        if (!idDataSet) {
            const country = getCountryFromIssuingAuthority(user.authority, countries);
            setNewIdDataSet(s => ({ ...s, country: country, issuingAuthority: user.authority }));
        }
    }, [idDataSet, countries, user.authority]);

    useEffect(() => {
        getSearchableIcs();
    }, [getSearchableIcs]);

    return (
        <Modal
            className="crew-member-modal"
            open={open}
            onClose={handleCloseModal}
            dimmer="inverted"
            closeOnDimmerClick={!loading}
            closeOnEscape={!loading}
        >
            <Modal.Header>
                <span>
                    {idDataSet
                        ? intl.formatMessage({ id: "editCrewMember" })
                        : intl.formatMessage({ id: "addCrewMember" })}
                </span>
                <Icon name="close" link size="large" onClick={handleCloseModal} />
            </Modal.Header>
            <Modal.Content>
                <div>
                    <form className="add-user-form">
                        <section className="input-container">
                            <label>
                                {intl.formatMessage({ id: "firstName" })}
                                <span className="input-required">*</span>
                            </label>
                            <Input
                                value={newIdDataSet.firstName}
                                onChange={(e: any) =>
                                    setNewIdDataSet({ ...newIdDataSet, firstName: e.target.value })
                                }
                            />
                        </section>
                        <section className="input-container">
                            <label>{intl.formatMessage({ id: "nonLatinFN" })}</label>
                            <Input
                                value={newIdDataSet.nonLatinFirstName}
                                onChange={(e: any) =>
                                    setNewIdDataSet({
                                        ...newIdDataSet,
                                        nonLatinFirstName: e.target.value,
                                    })
                                }
                            />
                        </section>
                        <section className="input-container">
                            <label>
                                {intl.formatMessage({ id: "lastName" })}
                                <span className="input-required">*</span>
                            </label>
                            <Input
                                value={newIdDataSet.lastName}
                                onChange={(e: any) =>
                                    setNewIdDataSet({ ...newIdDataSet, lastName: e.target.value })
                                }
                            />
                        </section>
                        <section className="input-container">
                            <label>{intl.formatMessage({ id: "nonLatinLN" })}</label>
                            <Input
                                value={newIdDataSet.nonLatinLastName}
                                onChange={(e: any) =>
                                    setNewIdDataSet({
                                        ...newIdDataSet,
                                        nonLatinLastName: e.target.value,
                                    })
                                }
                            />
                        </section>
                        <section className="input-container">
                            <label>
                                {intl.formatMessage({ id: "placeOfBirth" })}
                                <span className="input-required">*</span>
                            </label>
                            <Select
                                search
                                allowAdditions
                                additionLabel=""
                                clearable
                                loading={loadingSelect}
                                options={placeOfBirthOptions}
                                value={newIdDataSet.placeOfBirth}
                                onChange={(e, data: any) =>
                                    setNewIdDataSet({
                                        ...newIdDataSet,
                                        placeOfBirth: data.value,
                                    })
                                }
                                onAddItem={(e, data: any) => {
                                    setPlaceOfBirthOptions(placeOfBirthOptions => [
                                        ...placeOfBirthOptions,
                                        { text: data.value, value: data.value, key: data.value },
                                    ]);
                                }}
                            />
                        </section>
                        <section className="input-container">
                            <label>{intl.formatMessage({ id: "nonLatinPOB" })}</label>
                            <Select
                                search
                                allowAdditions
                                additionLabel=""
                                clearable
                                loading={loadingSelect}
                                options={placeOfBirthOptionsNonLat}
                                value={newIdDataSet.nonLatinPlaceOfBirth}
                                onChange={(e, data: any) =>
                                    setNewIdDataSet({
                                        ...newIdDataSet,
                                        nonLatinPlaceOfBirth: data.value,
                                    })
                                }
                                onAddItem={(e, data: any) => {
                                    setPlaceOfBirthOptionsNonLat(nonLatinPlaceOfBirth => [
                                        ...nonLatinPlaceOfBirth,
                                        { text: data.value, value: data.value, key: data.value },
                                    ]);
                                }}
                            />
                        </section>
                        <section className="input-container">
                            <label>{intl.formatMessage({ id: "stateOfBirth" })}</label>
                            <Select
                                selectOnBlur={false}
                                search
                                options={TranslateCountryProps(countries, intl.locale)}
                                value={newIdDataSet.stateOfBirth}
                                onChange={(e, data: any) =>
                                    setNewIdDataSet({
                                        ...newIdDataSet,
                                        stateOfBirth: +data.value,
                                    })
                                }
                            />
                        </section>
                        <section className="input-container">
                            <label>
                                {intl.formatMessage({ id: "dateOfBirth" })}
                                <span className="input-required">*</span>
                            </label>
                            <DateInput date={dateOfBirth} setDate={setDateOfBirth} selectOnBlur />
                        </section>
                        <section className="input-container">
                            <label>{intl.formatMessage({ id: "nationalityForIC" })}</label>
                            <Select
                                selectOnBlur={false}
                                search
                                options={TranslateCountryProps(nationalities, intl.locale, true)}
                                value={newIdDataSet.nationality}
                                onChange={(e, data: any) =>
                                    setNewIdDataSet({ ...newIdDataSet, nationality: +data.value })
                                }
                            />
                        </section>
                    </form>
                    <section className="input-container" style={{ marginTop: "15px" }}>
                        <label>{intl.formatMessage({ id: "email" })}</label>
                        <Input
                            value={newIdDataSet.email}
                            onChange={(e: any) =>
                                setNewIdDataSet({ ...newIdDataSet, email: e.target.value })
                            }
                        />
                    </section>
                    <Divider horizontal>{intl.formatMessage({ id: "residenceInfo" })}</Divider>
                    <form className="add-user-form">
                        <section className="input-container">
                            <label>{intl.formatMessage({ id: "street" })}</label>
                            <Input
                                value={newIdDataSet.street}
                                onChange={(e: any) =>
                                    setNewIdDataSet({ ...newIdDataSet, street: e.target.value })
                                }
                            />
                        </section>
                        <section className="input-container">
                            <label>{intl.formatMessage({ id: "zipCode" })}</label>
                            <Select
                                search
                                allowAdditions
                                additionLabel=""
                                clearable
                                loading={loadingSelect}
                                options={zipCodeOptions}
                                value={newIdDataSet.zipCode}
                                onChange={(e, data: any) =>
                                    setNewIdDataSet({
                                        ...newIdDataSet,
                                        zipCode: data.value,
                                    })
                                }
                                onAddItem={(e, data: any) => {
                                    setZipCodeOptions(zipCodeOptions => [
                                        ...zipCodeOptions,
                                        { text: data.value, value: data.value, key: data.value },
                                    ]);
                                }}
                            />
                        </section>
                        <section className="input-container">
                            <label>{intl.formatMessage({ id: "city" })}</label>
                            <Select
                                search
                                allowAdditions
                                additionLabel=""
                                clearable
                                loading={loadingSelect}
                                options={cityOptions}
                                value={newIdDataSet.city}
                                onChange={(e, data: any) =>
                                    setNewIdDataSet({
                                        ...newIdDataSet,
                                        city: data.value,
                                    })
                                }
                                onAddItem={(e, data: any) => {
                                    setCityOptions(cityOptions => [
                                        ...cityOptions,
                                        { text: data.value, value: data.value, key: data.value },
                                    ]);
                                }}
                            />
                        </section>
                        <section className="input-container">
                            <label>{intl.formatMessage({ id: "country" })}</label>
                            <Select
                                selectOnBlur={false}
                                search
                                options={TranslateCountryProps(countries, intl.locale)}
                                value={newIdDataSet.country}
                                onChange={(e, data: any) =>
                                    setNewIdDataSet({ ...newIdDataSet, country: +data.value })
                                }
                            />
                        </section>
                    </form>
                    <Divider horizontal>{intl.formatMessage({ id: "eidasInfo" })}</Divider>
                    <div id="eidas-container">
                        {newIdDataSet.eidasInformation.map((ei, index) => (
                            <section
                                className="input-container"
                                id="add-person-identifier"
                                key={index}
                            >
                                <label>
                                    {intl.formatMessage({ id: "personId" })} #{index + 1}
                                </label>
                                <Input
                                    value={ei.personIdentifier ?? ""}
                                    onChange={e => handleEidasInformationInputChange(e, index)}
                                />
                            </section>
                        ))}
                        <Button
                            id="add-person-identifier"
                            content={intl.formatMessage({ id: "addNewPersonID" })}
                            basic
                            fluid
                            primary
                            onClick={handleAddNewEidasInformation}
                            disabled={newIdDataSet.eidasInformation.some(
                                x => x.personIdentifier === ""
                            )}
                        />
                    </div>
                </div>
                <ImageDropzoneCropper crop={crop} setCrop={setCrop} />
            </Modal.Content>
            <Modal.Actions>
                <Button
                    primary
                    onClick={() => (idDataSet ? handleEditCrewMember() : handleAddCrewMember())}
                    content={
                        idDataSet
                            ? intl.formatMessage({ id: "saveChanges" })
                            : intl.formatMessage({ id: "add" })
                    }
                    fluid
                    loading={loading}
                    disabled={
                        loading ||
                        Object.values(dateOfBirth).some(x => !x || x === -1) ||
                        !newIdDataSet.firstName ||
                        !newIdDataSet.lastName ||
                        !newIdDataSet.placeOfBirth ||
                        (!idDataSet && !crop)
                    }
                />
            </Modal.Actions>
        </Modal>
    );
};
